import { APP } from 'src/app/config/app';
import { EventEmitter, Injectable } from '@angular/core';
import {
  IServiceInfo,
  IServiceWizardQuickCreation,
  IServiceWizardSetup,
  IServiceWizardSetupPayload,
  IWizardStepItem,
} from 'src/app/models';
import {
  NotificationTypes,
  RouteReorganizationType,
  ServiceType,
  ServiceWizardStep,
} from 'src/app/models/enums';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { images } from 'src/images';
import { objectToCamel } from 'ts-case-convert';
import { AuthService } from '../auth/auth.service';
import { LiteralService } from '../literal/literal.service';
import { RouteService } from '../routes/route.service';
import { UtilsService } from '../utils/utils.service';
import { WebSocketService } from '../web-socket/web-socket.service';

@Injectable({ providedIn: 'root' })
export class ServiceWizardService {
  public active = false;
  public step = ServiceWizardStep.SERVICE;
  public roundTrip = false;
  public canContinueEvaluator: () => boolean = () => false;
  public continueLabelKey = 'continue';
  public readonly continueEventEmitter = new EventEmitter<void>();
  public readonly backEventEmitter = new EventEmitter<void>();
  public pendingQuickCreation: IServiceWizardQuickCreation | null = null;
  public pendingWizardSetup: IServiceWizardSetup | null = null;
  public pendingWizardBackwardsSetup: IServiceWizardSetup | null = null;
  public reorganization: RouteReorganizationType | null = null;
  public reorganizationRoutes: any[] = [];
  public reorganizationSetups: (IServiceWizardSetup | null)[] = [];
  public reorganizationIndex = 0;

  constructor(
    private routeService: RouteService,
    private utilsService: UtilsService,
    private literalService: LiteralService,
    private authService: AuthService,
    private webSocketService: WebSocketService,
  ) {}

  get canContinue(): boolean {
    return this.canContinueEvaluator();
  }

  get steps(): ServiceWizardStep[] {
    if (this.reorganization)
      return [
        ServiceWizardStep.REORGANIZATION_SOURCES,
        ServiceWizardStep.FORWARD_ROUTE,
        ServiceWizardStep.FORWARD_SETUP,
        ServiceWizardStep.REORGANIZATION_SUMMARY,
      ];
    return this.roundTrip
      ? [
          ServiceWizardStep.SERVICE,
          ServiceWizardStep.FORWARD_ROUTE,
          ServiceWizardStep.BACKWARDS_ROUTE,
          ServiceWizardStep.FORWARD_SETUP,
          ServiceWizardStep.BACKWARDS_SETUP,
        ]
      : [
          ServiceWizardStep.SERVICE,
          ServiceWizardStep.FORWARD_ROUTE,
          ServiceWizardStep.FORWARD_SETUP,
        ];
  }

  get isFirstStep(): boolean {
    return this.step === this.steps[0];
  }

  get isLastStep(): boolean {
    return this.steps[this.steps.length - 1] === this.step;
  }

  get isSetupStep(): boolean {
    return (
      this.step === ServiceWizardStep.FORWARD_SETUP ||
      this.step === ServiceWizardStep.BACKWARDS_SETUP
    );
  }

  isPreviousToCurrentStep(step: ServiceWizardStep): boolean {
    const currentStepIndex = this.steps.indexOf(this.step);
    return this.steps.indexOf(step) < currentStepIndex;
  }

  // A reorganization designs and sets up as many new routes as it creates, one step each
  get stepItems(): IWizardStepItem[] {
    if (!this.reorganization)
      return this.steps.map((step) => ({
        id: step,
        labelKey: this.stepLabelKey(step),
      }));
    const count = this.reorganizationRoutesCount;
    const perRoute = (
      step: ServiceWizardStep,
      single: string,
      numbered: string,
    ) =>
      Array.from({ length: count }, (_, index) => ({
        id: this.reorganizationStepId(step, index),
        labelKey: count > 1 ? numbered : single,
        labelParams: { number: index + 1 },
      }));
    return [
      {
        id: ServiceWizardStep.REORGANIZATION_SOURCES,
        labelKey: 'reorganization.sources',
      },
      ...perRoute(
        ServiceWizardStep.FORWARD_ROUTE,
        'reorganization.route',
        'reorganization.routeNumber',
      ),
      ...perRoute(
        ServiceWizardStep.FORWARD_SETUP,
        'reorganization.setup',
        'reorganization.setupNumber',
      ),
      {
        id: ServiceWizardStep.REORGANIZATION_SUMMARY,
        labelKey: 'reorganization.summary',
      },
    ];
  }

  get currentStepId(): string {
    return this.reorganization
      ? this.reorganizationStepId(this.step, this.reorganizationIndex)
      : this.step;
  }

  isPreviousToCurrentStepId(id: string): boolean {
    const ids = this.stepItems.map((item) => item.id);
    return ids.indexOf(id) < ids.indexOf(this.currentStepId);
  }

  private reorganizationStepId(step: ServiceWizardStep, index: number): string {
    return step === ServiceWizardStep.FORWARD_ROUTE ||
      step === ServiceWizardStep.FORWARD_SETUP
      ? `${step}:${index}`
      : step;
  }

  get reorganizationRoutesCount(): number {
    if (this.reorganizationRoutes.length)
      return this.reorganizationRoutes.length;
    return this.reorganization === RouteReorganizationType.SPLIT
      ? APP.REORGANIZATION_MIN_SPLIT_ROUTES
      : 1;
  }

  get isLastReorganizationRoute(): boolean {
    return this.reorganizationIndex >= this.reorganizationRoutesCount - 1;
  }

  stepLabelKey(step: ServiceWizardStep): string {
    if (!this.roundTrip && step === ServiceWizardStep.FORWARD_ROUTE)
      return 'route';
    if (!this.roundTrip && step === ServiceWizardStep.FORWARD_SETUP)
      return 'setup';
    return step;
  }

  start() {
    this.active = true;
    this.step = ServiceWizardStep.SERVICE;
    this.roundTrip = false;
    this.reorganization = null;
    this.reorganizationRoutes = [];
    this.reorganizationSetups = [];
    this.reorganizationIndex = 0;
    this.pendingQuickCreation = null;
    this.pendingWizardSetup = null;
    this.pendingWizardBackwardsSetup = null;
    this.continueLabelKey = 'continue';
  }

  routeStepPath(backwards = false): any[] {
    if (this.reorganization) return this.reorganizationRoutePath();
    return ['/services', 'new', backwards ? 'backwards-route' : 'route'];
  }

  setupStepPath(backwards = false): any[] {
    if (this.reorganization) return this.reorganizationSetupPath();
    return ['/services', 'new', 'setup', ...(backwards ? ['backwards'] : [])];
  }

  reorganizationRoutePath(index = this.reorganizationIndex): any[] {
    return [...this.reorganizationPath, 'route', index + 1];
  }

  reorganizationSetupPath(index = this.reorganizationIndex): any[] {
    return [...this.reorganizationPath, 'setup', index + 1];
  }

  private get reorganizationPath(): any[] {
    return [
      '/services',
      this.pendingQuickCreation?.serviceInfo?.id,
      'reorganize',
    ];
  }

  startReorganization(
    type: RouteReorganizationType,
    quickCreation: IServiceWizardQuickCreation | null = null,
    routes: any[] = [],
  ) {
    this.start();
    this.reorganization = type;
    this.step = ServiceWizardStep.REORGANIZATION_SOURCES;
    this.pendingQuickCreation = quickCreation;
    this.reorganizationRoutes = routes;
    this.reorganizationSetups = routes.map(() => null);
  }

  setStep(step: ServiceWizardStep) {
    this.step = step;
  }

  // Reactivation while navigating between wizard steps: unlike start(), the pending
  // creation and setup data are kept
  restore(
    step: ServiceWizardStep,
    reorganizationIndex = this.reorganizationIndex,
  ) {
    this.active = true;
    this.step = step;
    this.reorganizationIndex = reorganizationIndex;
    this.roundTrip =
      !this.reorganization &&
      !!this.pendingQuickCreation?.serviceInfo?.roundTrips;
  }

  clear() {
    this.reorganization = null;
    this.reorganizationRoutes = [];
    this.reorganizationSetups = [];
    this.reorganizationIndex = 0;
    this.pendingQuickCreation = null;
    this.pendingWizardSetup = null;
    this.pendingWizardBackwardsSetup = null;
    this.stop();
  }

  stop() {
    // pendingQuickCreation is kept: it survives page changes so the wizard can be
    // restored when coming back from the setup screen
    this.active = false;
    this.step = ServiceWizardStep.SERVICE;
    this.canContinueEvaluator = () => false;
    this.continueLabelKey = 'continue';
  }

  async submitQuickCreation(
    setup: IServiceWizardSetupPayload | null,
    backwardsSetup: IServiceWizardSetupPayload | null = null,
    onProgress?: (percentage: number, detail: string) => void,
  ): Promise<IServiceInfo | null> {
    const pending = this.pendingQuickCreation;
    if (!pending) return null;
    const serviceInfo = pending.serviceInfo;
    const discretionary = serviceInfo.serviceType === ServiceType.DISCRETIONARY;
    const roundTrip = !!serviceInfo.roundTrips;
    const payload: any = {
      town:
        serviceInfo.townId > 0
          ? { id: serviceInfo.townId, clientId: serviceInfo.clientId }
          : { clientId: serviceInfo.clientId },
      serviceInfo: {
        name:
          discretionary || !serviceInfo.name
            ? serviceInfo.tag
            : serviceInfo.name,
        tag: serviceInfo.tag,
        serviceType: serviceInfo.serviceType,
        roundTrip,
        forwardDirection: serviceInfo.forwardDirection || null,
        backwardsDirection: serviceInfo.backwardsDirection || null,
        color: serviceInfo.color || null,
        sortCriteria: serviceInfo.sortCriteria,
      },
      service: this.buildServicePayload(pending.route, serviceInfo.clientId),
      setup: this.buildSetupPayload(setup),
      backwardsService:
        roundTrip && pending.backwardsRoute
          ? this.buildServicePayload(
              pending.backwardsRoute,
              serviceInfo.clientId,
            )
          : null,
      backwardsSetup: roundTrip ? this.buildSetupPayload(backwardsSetup) : null,
    };
    try {
      const operationId = await this.routeService.createQuickService(payload);
      const ephemeralToken =
        (await this.authService.getEphemeralToken()) as string;
      return await this.trackQuickCreation(
        operationId,
        ephemeralToken,
        onProgress,
      );
    } catch (error: any) {
      this.showQuickCreationError(error);
      return null;
    }
  }

  private async trackQuickCreation(
    operationId: string,
    ephemeralToken: string,
    onProgress?: (percentage: number, detail: string) => void,
  ): Promise<IServiceInfo | null> {
    const outcome = await this.webSocketService.trackOperation(
      operationId,
      ephemeralToken,
      (message) =>
        onProgress?.(
          message.total_percentage,
          this.webSocketService.progressDetail(message),
        ),
      async () => (await this.authService.getEphemeralToken()) as string,
    );
    if (outcome.error) {
      this.showQuickCreationError(outcome.error);
      return null;
    }
    return this.finishQuickCreation(outcome.result);
  }

  private finishQuickCreation(result: any): IServiceInfo | null {
    const response: any = objectToCamel(result);
    this.utilsService.showNotification(
      images.notifications.check,
      'services.actions.createService',
      NotificationTypes.SUCCESS,
    );
    this.pendingQuickCreation = null;
    this.pendingWizardSetup = null;
    this.pendingWizardBackwardsSetup = null;
    return response?.serviceInfo ?? null;
  }

  private showQuickCreationError(error: any) {
    if (error?.error_code) {
      const title = this.literalService.get(
        `${error.error_code}_ERROR.title`,
        true,
      );
      let text = this.literalService.get(
        `${error.error_code}_ERROR.text`,
        true,
      );
      switch (error.error_code) {
        case ErrorsCode.START_OR_END_ON_DYNAMIC:
          text = this.literalService.get(
            `${error.error_code}_ERROR.${error.entity_info}Text`,
            true,
          );
          break;
        case ErrorsCode.INVALID_REQUEST:
          text = this.literalService.get(
            `${error.error_code}_ERROR.text`,
            true,
            { fieldName: error.entity_info },
          );
          break;
      }
      this.utilsService.showNotification(
        images.notifications.warning,
        '',
        NotificationTypes.DANGER,
        title,
        text,
        undefined,
        false,
      );
    } else {
      this.utilsService.showNotification(
        images.notifications.warning,
        'services.actions.createService',
        NotificationTypes.DANGER,
      );
    }
  }

  private buildServicePayload(route: any, managerClientId: number) {
    return {
      terrain: route.terrain,
      outline: route.outline ?? null,
      name: route.name || null,
      managerClientId,
      maxTime: route.maxTime || null,
      expeditionType: route.expeditionType || null,
      visibility: route.visibility || null,
      showBusPosition: route.showBusPosition ?? null,
      showUserDataToDriver: route.showUserDataToDriver ?? null,
      singleStopBookings: route.singleStopBookings ?? null,
      criteria: route.criteria || null,
      maxMergeDistance: route.maxMergeDistance ?? null,
      ticketingType: route.ticketingType || null,
      sms: route.sms ?? null,
      push: route.push ?? null,
      email: route.email ?? null,
      whatsapp: route.whatsapp ?? null,
      notifyCommunicatedChanges: route.notifyCommunicatedChanges ?? null,
      mergedStopSideSwap: route.mergedStopSideSwap ?? null,
      informative: route.informative ?? null,
      dynamicStartStop: route.dynamicStartStop ?? null,
      dynamicEndStop: route.dynamicEndStop ?? null,
      apps: route.apps ?? [],
      fare: route.fare ?? null,
      saeIntegration: route.saeIntegration ?? null,
      saeActions: route.saeActions ?? [],
    };
  }

  setupPayload(
    setup: IServiceWizardSetup | null,
  ): IServiceWizardSetupPayload | null {
    const setupDetail = setup?.setupDetail;
    if (!setupDetail) return null;
    const scheduleRow = (row: any) => ({
      startTime: row.startTime,
      endTime: row.endTime,
      driver: { id: row.driver?.id, name: row.driver?.name ?? '' },
      vehicle: { id: row.vehicle?.id, name: row.vehicle?.name ?? '' },
      comment: row.comment ?? null,
    });
    return {
      name: setupDetail.name ?? 'Default',
      fromDate: setupDetail.fromDate,
      toDate: setupDetail.toDate,
      config: setupDetail.config,
      daysOfWeek: (setupDetail.daysOfWeek ?? []).map((dayOfWeek: any) => ({
        dayOfWeek: dayOfWeek.dayOfWeek,
        ...scheduleRow(dayOfWeek),
      })),
      holyDays: (setupDetail.holyDays ?? []).map((holyDay: any) => ({
        holyDayType: holyDay.holyDayType,
        ...scheduleRow(holyDay),
      })),
      departures: (setup.departures ?? []).map((departure: any) => ({
        departureDateTime: departure.departureDateTime,
        vehicleId: departure.vehicleId,
        driverId: departure.driverId,
      })),
    };
  }

  private buildSetupPayload(setup: IServiceWizardSetupPayload | null) {
    if (!setup) return null;
    return {
      name: setup.name ?? 'Default',
      fromDate: setup.fromDate ?? null,
      toDate: setup.toDate ?? null,
      config: setup.config ?? null,
      departures: setup.departures ?? [],
      daysOfWeek: setup.daysOfWeek ?? [],
      holyDays: setup.holyDays ?? [],
    };
  }
}
